import { useScrollToTarget } from "@becon-team/becon-fe-lib/hooks";
import { PageModal, useModal } from "@becon-team/becon-fe-lib/my-modal";
import { sleep } from "@becon-team/becon-fe-lib/utils";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { AccordionBtn } from "components/(button)/AccordionBtn";
import { GroupBtnFormInput } from "components/(form)/GroupBtnFormInput";
import { InquiryFormLayout } from "components/(form)/InquiryFormLayout";
import { SelectFormInput } from "components/(form)/SelectFormInput";
import { modalIds } from "libs/my-modal";
import { useGetAccountInfo } from "api/@auth/query_auth";
import { checkHideRaceInquiry } from "../utils/checkHideRaceInquiry";
import { useFormPutUserInquiry } from "../hooks/useFormPutUserInquiry";

import { Accordion } from "@becon-team/becon-fe-lib/components";
import { cn } from "@becon-team/becon-fe-lib/my-tailwind-css";
import { Undefinable } from "@becon-team/becon-fe-lib/my-types";

type ModalParams = {
  userId: string;
  onSuccess?: () => void;
  allowUnchangedSubmit?: boolean;
};

export const UserInquiryModal = () => {
  const modalId = modalIds["user-inquiry"];
  const { t } = useTranslation();

  const { modal, closeModal } = useModal(modalId);
  const { isOpened, params } = modal;
  const { userId, onSuccess, allowUnchangedSubmit = false } = (params as Undefinable<ModalParams>) || {};

  const [isOpenedAdditional, setIsOpenedAdditional] = useState(false);
  const { targetRef, scrollToTarget } = useScrollToTarget();
  const handleOpenAdditional = async () => {
    setIsOpenedAdditional(true);
    await sleep(150);
    scrollToTarget();
  };

  const { data: account } = useGetAccountInfo();

  const {
    genderOptions,
    ageGroupOptions,
    hairlossTypeAndLevelOptions,
    hairLossFamilyHistoryOptions,
    scalpIssuesOptions,
    hairDamageLevelOptions,
    skinColorOptions,
    hairTypeOptions,
    hairColorOptions,
    preferenceShampooOptions,
    preferenceHairOptions,
    dyeingOptions,
    permOptions,
    washHairCycleOptions,
    treatmentProductOptions,
    dietOptions,
    smokingOptions,
    coffeeOptions,
    drinkingOptions,
    workOutOptions,
    sleepTimeOptions,
    stressLevelOptions,
    genderRegister,
    ageGroupRegister,
    hairlossTypeAndLevelRegister,
    hairlossFamilyHistoryRegister,
    scalpIssuesRegister,
    hairDamageLevelRegister,
    skinColorRegister,
    hairTypeRegister,
    hairColorRegister,
    preferenceHairRegister,
    preferenceShampooRegister,
    dyeingRegister,
    permRegister,
    washHairCycleRegister,
    treatmentProductRegister,
    dietRegister,
    smokingRegister,
    coffeeRegister,
    drinkingRegister,
    workOutRegister,
    sleepTimeRegister,
    stressLevelRegister,
    errorMessages,
    onSubmit,
    isSubmitting,
    isFormChanged,
  } = useFormPutUserInquiry({
    userId,
    onSuccess: () => {
      closeModal({
        onFinish: () => {
          onSuccess && onSuccess();
        },
      });
    },
  });

  const hideRaceInquiry = checkHideRaceInquiry(account.data.account.region);

  return (
    <PageModal
      closeModal={closeModal}
      titleSection={t("문진 작성")}
      btnSection={
        <button
          form={modalId}
          className={cn("my-btn-primary w-full", { "my-loading": isSubmitting })}
          type="submit"
          disabled={!isFormChanged && !allowUnchangedSubmit}
        >
          {t("제출하기")}
        </button>
      }
      isOpened={isOpened}
    >
      <form id={modalId} onSubmit={onSubmit}>
        <div className="space-y-sp-40 p-inner-padding pt-sp-16">
          <InquiryFormLayout
            number="01"
            label={t("탈모 상태를 선택해 주세요")}
            errorMessage={errorMessages.hairlossTypeAndLevel}
            isRequired={true}
          >
            <GroupBtnFormInput
              className="grid-cols-2 md:grid-cols-3"
              options={hairlossTypeAndLevelOptions}
              registerProps={hairlossTypeAndLevelRegister}
            />
          </InquiryFormLayout>

          <InquiryFormLayout
            number="02"
            label={t("성별을 선택해 주세요")}
            errorMessage={errorMessages.gender}
            isRequired={true}
          >
            <GroupBtnFormInput className="grid-cols-2" options={genderOptions} registerProps={genderRegister} />
          </InquiryFormLayout>

          <InquiryFormLayout
            number="03"
            label={t("나이대를 선택해 주세요")}
            errorMessage={errorMessages.ageGroup}
            isRequired={true}
          >
            <SelectFormInput options={ageGroupOptions} registerProps={ageGroupRegister} disableEmptyOptions={true} />
          </InquiryFormLayout>

          <InquiryFormLayout
            number="04"
            label={t("탈모 가족력이 있으신가요?")}
            errorMessage={errorMessages.hairlossFamilyHistory}
            isRequired={true}
          >
            <GroupBtnFormInput
              className="grid-cols-2 md:grid-cols-3"
              options={hairLossFamilyHistoryOptions}
              registerProps={hairlossFamilyHistoryRegister}
            />
          </InquiryFormLayout>

          <InquiryFormLayout
            number="05"
            label={t("두피 고민을 선택해 주세요")}
            info={t("최대 {{n}}개까지 선택할 수 있어요", { n: 3 })}
            errorMessage={errorMessages.scalpIssues}
            isRequired={true}
          >
            <GroupBtnFormInput
              className="grid-cols-2 md:grid-cols-4"
              options={scalpIssuesOptions}
              registerProps={scalpIssuesRegister}
            />
          </InquiryFormLayout>

          <InquiryFormLayout
            number="06"
            label={t("모발 손상도를 선택해 주세요")}
            errorMessage={errorMessages.hairDamageLevel}
            isRequired={true}
          >
            <GroupBtnFormInput
              className="grid-cols-2 md:grid-cols-4"
              options={hairDamageLevelOptions}
              registerProps={hairDamageLevelRegister}
            />
          </InquiryFormLayout>

          {/* // *정책: 일부 국가는 피부톤, 모발 색, 모발 형태를 선택하지 않음 (기본값: 황색, 흑발, 직모) */}
          {!hideRaceInquiry && (
            <>
              <InquiryFormLayout
                number="07"
                label={t("피부톤을 선택해 주세요")}
                errorMessage={errorMessages.skinColor}
                isRequired={true}
              >
                <GroupBtnFormInput
                  className="grid-cols-2 md:grid-cols-3"
                  options={skinColorOptions}
                  registerProps={skinColorRegister}
                />
              </InquiryFormLayout>

              <InquiryFormLayout
                number="08"
                label={t("모발 색을 선택해 주세요")}
                errorMessage={errorMessages.hairColor}
                isRequired={true}
              >
                <GroupBtnFormInput
                  className="grid-cols-2 md:grid-cols-4"
                  options={hairColorOptions}
                  registerProps={hairColorRegister}
                />
              </InquiryFormLayout>

              <InquiryFormLayout
                number="09"
                label={t("모발 형태를 선택해 주세요")}
                errorMessage={errorMessages.hairType}
                isRequired={true}
              >
                <GroupBtnFormInput
                  className="grid-cols-2 md:grid-cols-4"
                  options={hairTypeOptions}
                  registerProps={hairTypeRegister}
                />
              </InquiryFormLayout>
            </>
          )}

          <Accordion className="flex justify-center overflow-hidden" isOpen={!isOpenedAdditional}>
            <AccordionBtn
              title={t("추가 문진")}
              isOpened={isOpenedAdditional}
              handleClose={() => setIsOpenedAdditional(false)}
              handleOpen={handleOpenAdditional}
            />
          </Accordion>
        </div>

        <div ref={targetRef} />

        <Accordion
          className={cn("border-t-4 border-base-300 bg-base-50 p-inner-padding", { hidden: !isOpenedAdditional })}
          isOpen={isOpenedAdditional}
        >
          <div className="text-lg font-light text-base-600">*{t("여기서부터는 필수가 아닌 추가 문진이에요")}</div>
          <div className="mt-sp-24 space-y-sp-40">
            <InquiryFormLayout
              number={hideRaceInquiry ? "07" : "10"}
              label={t("선호하는 샴푸 특징이 있나요?")}
              info={t("최대 {{n}}개까지 선택할 수 있어요", { n: 2 })}
              errorMessage={errorMessages.preferenceShampoo}
            >
              <GroupBtnFormInput
                className="grid-cols-2 md:grid-cols-4"
                options={preferenceShampooOptions}
                registerProps={preferenceShampooRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "08" : "11"}
              label={t("샴푸 후 어떤 머릿결을 선호하시나요?")}
              errorMessage={errorMessages.preferenceShampoo}
            >
              <GroupBtnFormInput
                className="grid-cols-2"
                options={preferenceHairOptions}
                registerProps={preferenceHairRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "09" : "12"}
              label={t("염색을 1년에 얼마나 하시나요?")}
              errorMessage={errorMessages.dyeing}
            >
              <GroupBtnFormInput className="grid-cols-2" options={dyeingOptions} registerProps={dyeingRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "10" : "13"}
              label={t("펌을 1년에 얼마나 하시나요?")}
              errorMessage={errorMessages.perm}
            >
              <GroupBtnFormInput className="grid-cols-2" options={permOptions} registerProps={permRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "11" : "14"}
              label={t("트리트먼트 또는 린스를 사용하시나요?")}
              errorMessage={errorMessages.treatmentProduct}
            >
              <GroupBtnFormInput
                className="grid-cols-2"
                options={treatmentProductOptions}
                registerProps={treatmentProductRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "12" : "15"}
              label={t("샴푸 주기가 어떻게 되시나요?")}
              errorMessage={errorMessages.washHairCycle}
            >
              <GroupBtnFormInput
                className="grid-cols-2"
                options={washHairCycleOptions}
                registerProps={washHairCycleRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "13" : "16"}
              label={t("다이어트를 하시고 있나요?")}
              errorMessage={errorMessages.diet}
            >
              <GroupBtnFormInput className="grid-cols-2" options={dietOptions} registerProps={dietRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "14" : "17"}
              label={t("흡연을 얼마나 하시나요?")}
              errorMessage={errorMessages.smoking}
            >
              <GroupBtnFormInput
                className="grid-cols-2 md:grid-cols-3"
                options={smokingOptions}
                registerProps={smokingRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "15" : "18"}
              label={t("커피를 얼마나 마시나요?")}
              errorMessage={errorMessages.coffee}
            >
              <GroupBtnFormInput
                className="grid-cols-2 md:grid-cols-3"
                options={coffeeOptions}
                registerProps={coffeeRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "16" : "19"}
              label={t("음주를 얼마나 하시나요?")}
              errorMessage={errorMessages.drinking}
            >
              <GroupBtnFormInput
                className="grid-cols-2 md:grid-cols-3"
                options={drinkingOptions}
                registerProps={drinkingRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "17" : "20"}
              label={t("운동을 얼마나 하시나요?")}
              errorMessage={errorMessages.workOut}
            >
              <GroupBtnFormInput
                className="grid-cols-2 md:grid-cols-3"
                options={workOutOptions}
                registerProps={workOutRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "18" : "21"}
              label={t("하루에 얼마나 주무시나요?")}
              errorMessage={errorMessages.sleepTime}
            >
              <GroupBtnFormInput className="grid-cols-2" options={sleepTimeOptions} registerProps={sleepTimeRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout
              number={hideRaceInquiry ? "19" : "22"}
              label={t("스트레스 정도를 선택해 주세요")}
              errorMessage={errorMessages.stressLevel}
            >
              <GroupBtnFormInput
                className="grid-cols-2 md:grid-cols-4"
                options={stressLevelOptions}
                registerProps={stressLevelRegister}
              />
            </InquiryFormLayout>
          </div>
        </Accordion>
      </form>
    </PageModal>
  );
};

export const useUserInquiryModal = () => {
  const { modal, openModal, closeModal } = useModal(modalIds["user-inquiry"]);

  const openModalCallback = useCallback(
    (params: ModalParams) => {
      openModal(params);
    },
    [openModal],
  );

  return {
    openModal: openModalCallback,
    closeModal,
    isOpened: modal.isOpened,
  };
};
